import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { assertResolvedAtsNotHttpError } from '@/api/ats/atsEnvelope';
import { http } from '@/lib/ats-https';
import type { HiringTeamRole } from '@/types/interview';

// The job's hiring team, grouped by on-job role (coordinator / recruiter /
// hiring manager / …). Mirrors the interviewer-pool hook's shape:
// `http.get` resolves to the ats envelope body `{ data, meta, errors }`, so the
// team lives at `body.data.roles`.
export type HiringTeamOfJob = { roles: HiringTeamRole[] };

type HiringTeamApiResponse = { data: HiringTeamOfJob; errors?: string[] };

export const hiringTeamQueryKey = (jobId: string) =>
  ['hiring-team', jobId] as const;

/**
 * `GET /api_ats/v1/ats_jobs/:jobId/ats_hiring_teams`.
 *
 * React Query replacement for the (now-removed) RTK `getHiringTeamOfTheJob`
 * endpoint — the automation drawer's only consumer, and consistent with the
 * `useGetUserGroups` / `useUsersWithScope` hooks it already uses. The team is
 * job-specific and rarely changes mid-session, so cache it for 5 minutes.
 */
export const useGetHiringTeamOfJob = (
  jobId: string,
  options: { enabled?: boolean } = {}
): UseQueryResult<HiringTeamOfJob, Error> =>
  useQuery<HiringTeamOfJob, Error>({
    queryKey: hiringTeamQueryKey(jobId),
    queryFn: async () => {
      const res = await http.get<HiringTeamApiResponse>(
        `ats_jobs/${jobId}/ats_hiring_teams`
      );
      assertResolvedAtsNotHttpError(res);
      return (res as unknown as HiringTeamApiResponse).data;
    },
    enabled: options.enabled ?? Boolean(jobId),
    staleTime: 5 * 60 * 1000,
  });
